import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'utils/i18n';
import '../ResourceDetailModal/styles/MarketplacePicker.css';
import marketplaceTranslations from './translations';

/**
 * B-esper Provider Modal - Premium Palantir style with wavy line decorations
 * Shows comprehensive information about B-esper based on homepage content
 */
export const BesperProviderModal = ({ onClose }) => {
  const { language } = useTranslation();
  const canvasRef = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const animationRef = useRef(null);
  const timeRef = useRef(0);
  
  // Get translations - follows same pattern as other marketplace components
  const t = (key, defaultValue = '') => {
    const keys = key.split('.');
    let value = marketplaceTranslations[language] || marketplaceTranslations.en;
    for (const k of keys) {
      value = value?.[k];
    }
    return value || defaultValue || key;
  };
  
  // Initialize wavy lines canvas (onboarding-style)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;
    
    let animationFrameId;
    const isDarkMode = document.documentElement.getAttribute('data-theme') === 'dark';
    
    const resizeCanvas = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      ctx.scale(dpr, dpr);
    };
    
    const drawWavyLines = () => {
      const rect = canvas.getBoundingClientRect();
      const width = rect.width;
      const height = rect.height;
      
      ctx.clearRect(0, 0, width, height);
      
      const numLines = 4; // Fewer lines, more subtle
      const time = timeRef.current;
      
      // B-esper brand blue colors - very subtle
      const strokeColor = isDarkMode 
        ? 'rgba(88, 151, 222, 0.08)' // Very subtle in dark mode
        : 'rgba(2, 45, 84, 0.06)'; // Very subtle in light mode
      
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = 1;
      ctx.lineCap = 'round';
      
      for (let i = 0; i < numLines; i++) {
        const t = i / (numLines - 1);
        
        // Subtle lines flowing from top-left, contained within bounds
        const startX = -width * 0.1; // Start slightly off-screen
        const startY = t * height * 0.4; // Start in top 40%
        
        // Very subtle animated wave offset
        const waveOffset = Math.sin(time * 0.0005 + t * Math.PI * 2) * 8;
        const curveFactor = Math.sin(t * Math.PI) * 0.15;
        const endX = startX + (t * width * 0.5) + curveFactor * width * 0.05 + waveOffset;
        const endY = height * 1.1; // Extend slightly beyond
        
        // Control points for smooth bezier curves
        const ctrl1X = startX + width * 0.15;
        const ctrl1Y = startY + height * 0.2;
        const ctrl2X = endX - width * 0.05;
        const ctrl2Y = endY - height * 0.1;
        
        // Very low opacity
        ctx.globalAlpha = 0.15;
        
        // Draw smooth bezier curve
        ctx.beginPath();
        ctx.moveTo(startX, startY);
        ctx.bezierCurveTo(ctrl1X, ctrl1Y, ctrl2X, ctrl2Y, endX, endY);
        ctx.stroke();
      }
      
      ctx.globalAlpha = 1.0;
    };
    
    const animate = () => {
      timeRef.current += 16; // ~60fps
      drawWavyLines();
      animationFrameId = requestAnimationFrame(animate);
    };
    
    resizeCanvas();
    animate();
    
    const handleResize = () => {
      resizeCanvas();
      drawWavyLines();
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, []);
  
  // Hide technical interface when modal is open
  useEffect(() => {
    document.body.classList.add('hide-technical-interface');
    return () => {
      const hasOtherModals = document.querySelector(
        '.tenant-modal-overlay, .resource-modal-overlay, .costpool-modal-overlay, ' +
        '.user-detail-modal-overlay, .modal-overlay, .implementation-fullscreen-overlay, ' +
        '[class*="modal-overlay"], [class*="fullscreen-overlay"]'
      );
      if (!hasOtherModals) {
        document.body.classList.remove('hide-technical-interface');
      }
    };
  }, []);

  return (
    <div 
      className="besper-provider-modal-overlay palantir-modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        className="besper-provider-modal palantir-modal premium-besper-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Animated Wavy Lines Background - Subtle */}
        <canvas 
          ref={canvasRef} 
          className="besper-modal-wavy-canvas"
        />
        
        {/* Header - Premium Palantir Style with logo */}
        <div className="besper-modal-header premium-header" style={{ position: 'relative', zIndex: 1 }}>
          <div className="besper-modal-header-content">
            <div className="besper-logo-container">
              <div className="besper-logo-placeholder">
                <span className="besper-logo-letter">B</span>
              </div>
            </div>
            <div className="besper-header-text">
              <h2 className="besper-modal-title besper-premium-title">B-esper</h2>
              <p className="besper-modal-subtitle besper-premium-subtitle">
                {t('whatBesperDoes.paragraph1', 'B-esper provides the infrastructure for the B-esper Platform as well as native products on the marketplace, such as B-esper Bot - our AI-powered conversational chatbot that can be managed via API and UI, implemented on your website and connected easily to use as your knowledge base with daily updates.')}
              </p>
            </div>
          </div>
          <button
            className="besper-modal-close premium-close"
            onClick={onClose}
            aria-label={t('besperProviderModal.closeModal', 'Close modal')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        
        {/* Content - Premium Palantir Style */}
        <div className="besper-modal-content premium-content" style={{ position: 'relative', zIndex: 1 }}>
          {/* What We Do Section */}
          <section className="besper-section">
            <h3 className="besper-section-title">
              {t('whatBesperDoes.heading', 'What B-esper Provides')}
            </h3>
            <p className="besper-section-text">
              {t('whatBesperDoes.paragraph2', 'B-esper provides the infrastructure for the B-esper Platform, enabling third-party vendors to sell their SaaS products on our marketplace. We also offer native products such as B-esper Bot, with more products coming soon. Agencies can offer implementation and management services through our platform.')}
            </p>
          </section>

          {/* Core Services Grid */}
          <div className="besper-services-grid">
            {/* Cloud & Infrastructure */}
            <div className="besper-service-card">
              <div className="besper-service-icon">
                <svg width="48" height="48" viewBox="0 0 64 64" fill="none">
                  <path d="M20 36c0-5 4-9 9-9 1.5 0 3 0 4.5 1.5 3-4 7-5 11-5 6 0 11 5 11 11 0 2.5-1 4.5-2.5 6" stroke="#022d54" strokeWidth="2.5" strokeLinecap="round"/>
                  <path d="M44 40H30c-4 0-7-3-7-7s3-7 7-7c1.5-2.5 4-4 6.5-4 4 0 7 3 7 7 0 2.5-1.5 4-2.5 4z" stroke="#022d54" strokeWidth="2.5" strokeLinecap="round"/>
                  <circle cx="32" cy="32" r="4" fill="#022d54" opacity="0.25" stroke="#022d54"/>
                  <circle cx="24" cy="36" r="3" fill="#022d54"/>
                  <circle cx="40" cy="36" r="3" fill="#022d54"/>
                </svg>
              </div>
              <h4 className="besper-service-title">
                {t('whatBesperDoes.cloud.heading', 'Cloud & Infrastructure')}
              </h4>
              <p className="besper-service-description">
                {t('whatBesperDoes.cloud.description', 'Europe-first infrastructure strategy. We host on Azure in Germany, ensuring data sovereignty and compliance with European regulations.')}
              </p>
              <p className="besper-service-description">
                {t('whatBesperDoes.cloud.description2', "You'll be able to select where to host your data and compute. Migration to Kubernetes is planned for first-party hosted products on the platform.")}
              </p>
            </div>

            {/* B-esper Bot */}
            <div className="besper-service-card">
              <div className="besper-service-icon">
                <svg width="48" height="48" viewBox="0 0 64 64" fill="none">
                  <g stroke="#022d54" strokeWidth="2.5" strokeLinecap="round" fill="none">
                    <circle cx="32" cy="32" r="10" fill="#022d54" opacity="0.2" stroke="#022d54"/>
                    <circle cx="32" cy="32" r="5" fill="#022d54" opacity="0.4"/>
                    <circle cx="18" cy="24" r="3.5" fill="#022d54"/>
                    <circle cx="46" cy="24" r="3.5" fill="#022d54"/>
                    <circle cx="18" cy="40" r="3.5" fill="#022d54"/>
                    <circle cx="46" cy="40" r="3.5" fill="#022d54"/>
                    <line x1="32" y1="27" x2="18" y2="24" strokeWidth="2" opacity="0.6"/>
                    <line x1="32" y1="27" x2="46" y2="24" strokeWidth="2" opacity="0.6"/>
                    <line x1="32" y1="37" x2="18" y2="40" strokeWidth="2" opacity="0.6"/>
                    <line x1="32" y1="37" x2="46" y2="40" strokeWidth="2" opacity="0.6"/>
                  </g>
                </svg>
              </div>
              <h4 className="besper-service-title">
                {t('whatBesperDoes.ai.heading', 'B-esper Bot')}
              </h4>
              <p className="besper-service-description">
                {t('whatBesperDoes.ai.description', 'Our first in-house product. Managed via API and UI, easily implemented on your website.')}
              </p>
              <p className="besper-service-description">
                {t('whatBesperDoes.ai.description2', 'Connect it as your knowledge base with daily updates. Upload your own files. Easy website connection, simple implementation, and self-managing capabilities.')}
              </p>
            </div>

            {/* Platform & Infrastructure */}
            <div className="besper-service-card">
              <div className="besper-service-icon">
                <svg width="48" height="48" viewBox="0 0 64 64" fill="none">
                  <g stroke="#022d54" strokeWidth="2.5" strokeLinecap="round" fill="none">
                    <rect x="14" y="28" width="36" height="24" fill="#022d54" opacity="0.1" stroke="#022d54" rx="2"/>
                    <path d="M14 28 L32 18 L50 28" stroke="#022d54" strokeWidth="3" fill="#022d54" opacity="0.15"/>
                    <line x1="20" y1="34" x2="44" y2="34" strokeWidth="2" opacity="0.5"/>
                    <line x1="20" y1="40" x2="44" y2="40" strokeWidth="2" opacity="0.5"/>
                  </g>
                </svg>
              </div>
              <h4 className="besper-service-title">
                {t('whatBesperDoes.integration.heading', 'Platform & Marketplace')}
              </h4>
              <p className="besper-service-description">
                {t('whatBesperDoes.integration.description', 'B-esper provides the infrastructure for the B-esper Platform, enabling third-party vendors to sell their SaaS products through our marketplace.')}
              </p>
              <p className="besper-service-description">
                {t('whatBesperDoes.integration.description2', 'Partners can resell products and manage customers, while agencies offer implementation and management services through our comprehensive platform.')}
              </p>
            </div>
          </div>

          {/* Vision Section */}
          <section className="besper-section vision-section">
            <h3 className="besper-section-title">
              {t('vision.heading', 'Our Vision')}
            </h3>
            <p className="besper-section-text">
              {t('vision.description', 'Our vision is to offer a platform on which SME companies can manage all of their SaaS needs in a reliable and cost-efficient way and find trustworthy partners to work with.')}
            </p>
            
            <div className="besper-vision-cards">
              <div className="besper-vision-card">
                <h4 className="besper-vision-card-title">
                  {t('vision.forCustomers.heading', 'For Customers')}
                </h4>
                <p className="besper-vision-card-text">
                  {t('vision.forCustomers.description', 'Our self-service platform enables businesses to discover, purchase, and manage SaaS products independently. With minimal technical knowledge required, you can find the solutions you need and implement them directly through our streamlined platform, all while managing your entire SaaS portfolio in one place.')}
                </p>
              </div>
              
              <div className="besper-vision-card">
                <h4 className="besper-vision-card-title">
                  {t('vision.forPartners.heading', 'For Partners')}
                </h4>
                <p className="besper-vision-card-text">
                  {t('vision.forPartners.description', 'Partners can resell our products to their customers and manage both their customers and product portfolio through our comprehensive platform. Our platform provides centralized management capabilities for all partner operations.')}
                </p>
              </div>
            </div>
          </section>

          {/* Tags */}
          <div className="besper-tags-section">
            <span className="besper-tag">{t('besperProviderModal.tag.infrastructure', 'Infrastructure')}</span>
            <span className="besper-tag">{t('besperProviderModal.tag.marketplace', 'Marketplace')}</span>
            <span className="besper-tag">{t('besperProviderModal.tag.ai', 'AI')}</span>
            <span className="besper-tag">{t('besperProviderModal.tag.enterprise', 'Enterprise')}</span>
            <span className="besper-tag">{t('besperProviderModal.tag.cloud', 'Cloud')}</span>
            <span className="besper-tag">{t('besperProviderModal.tag.platform', 'Platform')}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

